import React, { useState, useEffect, useCallback } from 'react'

import Input from './input'
import Toast from 'Components/toast-messager'
import classNames from 'classnames'
import fetch from 'lib/decorated-fetch'
import formatTime from 'lib/format-time'
import getUrl from 'lib/url'
import safeGet from '@arsenal/arsenal/modules/safeGet'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'
import { emoji } from 'Constants/emoji'
import xss from 'xss'

import ModalConfirmUponSelf from 'Components/modal-confirm-upon-self'
import ModalReport, { IReportData } from 'Components/modal-report'
import useLoginCheckCallback from 'Components/hooks/use-login-check-callback'

export interface IReply {
  id_str: string
  create_time: number
  digg_count: number
  text: string
  user_digg: number
  user: {
    user_id: number
    name: string
    avatar_url: string
  }
  reply_to_comment?: IReply
}

interface IProps {
  info: IReply
  showUnderLine: boolean
  gid: string
  commentId: string
  submitComment?: (
    replyId: string,
    content: string,
    userId: string | number
  ) => Promise<void>
  isLogin: boolean
  userId: string | number
  eventBus: any
  deleteComment?: (commentId: string, rank: number, replyId: string) => void
  reportUser?: (
    commentId: string,
    reportTypes: string,
    description: string,
    reportUserId: number
  ) => void
  rank: number
}

const toward_top_icon = require('static/toward-top.png')
const thumb_up_icon = require('static/thumb-up.png')
const thumb_up_active_icon = require('static/thumb-up-active.png')
const show_more_icon = require('static/show-more.png')

const Reply = ({
  info,
  showUnderLine,
  submitComment,
  gid,
  commentId,
  isLogin,
  eventBus,
  userId,
  deleteComment,
  reportUser,
  rank,
}: IProps) => {
  const {
    create_time,
    user,
    text,
    digg_count = 0,
    id_str: replyId,
    reply_to_comment,
    user_digg,
  } = info

  const [showReplyInput, setShowReplyInput] = useState(false)
  const [userDigged, setUserDigged] = useState(!!user_digg)
  const [diggCount, setDiggCount] = useState(digg_count)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showReportConfirm, setShowReportConfirm] = useState(false)

  const handleReplyClick = useLoginCheckCallback(
    () => {
      if (!isLogin) {
        Toast.show({
          text: '请先登录再评论~', // 1
        })
        return
      }
      setShowReplyInput(!showReplyInput)
    },
    {
      enter_method: 'comment',
    }
  )

  function handleSubmitComment(commentText: string) {
    if (submitComment) {
      submitComment(replyId, commentText, user.user_id)
        .then(() => {
          setShowReplyInput(false)
        })
        .catch((e) => {
          if (e.message !== '回复内容不能为空！') console.error(e)
        })
    }
  }

  const handleDiggClick = useLoginCheckCallback(
    () => {
      if (!isLogin) {
        Toast.show({
          text: '请先登录~', // 1
        })
        return
      }
      const action = userDigged ? 'cancel_digg' : 'digg'
      // 先假设点赞成功
      setUserDigged(action === 'digg')
      setDiggCount(diggCount + (action === 'digg' ? 1 : -1))
      const diggCountBeforeChanged = diggCount
      const params = {
        aid: 1839,
        app_name: 'auto_web_pc',
        id: commentId, // 评论id
        reply_id: replyId, // 回复id
        item_id: gid,
        group_id: gid,
        action,
      }
      fetch(
        getUrl('/motor/proxy/digg_reply/browser/2/comment/v2/digg_reply/'),
        {
          method: 'POST',
          headers: {
            'tt-anti-token': window.localStorage.getItem('tt-anti-token') || '',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: serializeObject(params),
        }
      )
        .then((res) => {
          const { errno } = res
          if (errno !== 0) {
            Toast.show({
              text: '点赞失败',
            })

            setUserDigged(action !== 'digg')
            setDiggCount(diggCountBeforeChanged)
          }
        })
        .catch((err) => {
          console.log(err)
          // 如果失败了，重置点赞状态
          setUserDigged(action !== 'digg')
          setDiggCount(diggCountBeforeChanged)
        })
    },
    {
      enter_method: 'like',
    }
  )

  // 举报评论
  function handleReportClick() {
    setShowReportConfirm(!showReportConfirm)
  }

  function handleModalReportResult({ type, data = [] }: IReportData) {
    if (type === 'confirm') {
      const reportTypes: string[] = []
      const description: string[] = []

      data.forEach((item) => {
        reportTypes.push(item.value)
        if (item.description) {
          description.push(item.description)
        }
      })

      reportUser &&
        reportUser(
          commentId,
          reportTypes.join(','),
          description.join(''),
          user.user_id
        )
    }
    setShowReportConfirm(false)
  }

  function handleModalConfirmResult(type: 'confirm' | 'cancel') {
    if (type === 'confirm') {
      deleteComment && deleteComment(commentId, rank, replyId)
    }
    setShowDeleteConfirm(false)
  }

  function handleDeleteComment() {
    setShowDeleteConfirm(!showDeleteConfirm)
  }

  return (
    <div className="reply-item fade-in">
      <a
        // eslint-disable-next-line react/jsx-no-target-blank
        target="_blank"
        href={`/user/ugc_profile/${user.user_id}`}
      >
        <div
          className="avatar fr"
          style={{
            backgroundImage: `url(${user.avatar_url})`,
          }}
        />
      </a>

      <div
        className={classNames('content clearfix', {
          'under-line': showUnderLine,
        })}
      >
        <a
          // eslint-disable-next-line react/jsx-no-target-blank
          target="_blank"
          href={`/user/ugc_profile/${user.user_id}`}
        >
          <div className="top-info clearfix">
            <div className="name fl">{user.name}</div>
            <div className="time fr">{formatTime(create_time * 1000)}</div>
          </div>
        </a>

        <div className="text">
          <p
            className="text"
            dangerouslySetInnerHTML={{ __html: emoji(xss.filterXSS(text)) }}
          />
          {reply_to_comment ? (
            <>
              <span>//@{safeGet(reply_to_comment, 'user_name') || ''}：</span>
              <span
                dangerouslySetInnerHTML={{
                  __html: emoji(
                    xss.filterXSS(safeGet(reply_to_comment, 'text'))
                  ),
                }}
              />
            </>
          ) : null}
        </div>
        <div className="bottom-extra">
          <div className="reply fl" onClick={handleReplyClick}>
            回复
          </div>
          <div className="fr">
            {userId === user.user_id ? (
              <div className="fl delete-comment" onClick={handleDeleteComment}>
                删除
                {showDeleteConfirm && (
                  <ModalConfirmUponSelf
                    text={'确认删除此条回复？'}
                    onButtonClick={handleModalConfirmResult}
                  />
                )}
              </div>
            ) : (
              <div className="fl report" onClick={handleReportClick}>
                举报
              </div>
            )}
            <div
              className={classNames('digg fr', {
                digged: userDigged,
              })}
              onClick={handleDiggClick}
            >
              {diggCount || '赞'}
            </div>
          </div>
        </div>
        <div
          className={classNames('reply-input', {
            show: showReplyInput,
          })}
        >
          {showReplyInput ? (
            <Input text="回复" submitClick={handleSubmitComment} />
          ) : null}
        </div>
      </div>

      <ModalReport
        visible={showReportConfirm}
        onButtonClick={handleModalReportResult}
      />
      <style jsx>
        {`
          .fade-in {
            animation: fadeIn 0.3s ease;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .reply-item {
            position: relative;
            padding-left: 50px;
            font-size: 16px;
          }
          .avatar {
            position: absolute;
            top: 20px;
            left: 0;
            width: 40px;
            height: 40px;
            border: solid 1px #e6e6e6;
            border-radius: 50%;
            background-size: 100% 100%;
          }
          .content {
            position: relative;
            padding: 20px 0;
          }
          .reply-item:last-of-type .content {
            padding-bottom: 0;
          }
          .under-line::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 1px;
            background-color: #e6e8f2;
          }
          .reply-item:last-of-type .content::after {
            height: 0;
          }
          .top-info {
            margin-bottom: 16px;
            line-height: 22px;
            color: #999999;
          }
          .name {
            color: #606370;
          }
          .time {
            color: #979aa8;
          }
          .text {
            line-height: 28px;
            color: #1f2129;
            margin-bottom: 20px;
          }
          .reply-count {
            position: relative;
            color: #205be6;
            cursor: pointer;
          }
          .reply-count::after {
            content: '';
            position: absolute;
            top: 50%;
            transform: translateY(-50%) rotate(180deg);
            right: -16px;
            width: 16px;
            height: 16px;
            background-image: url('${toward_top_icon}');
            background-size: 100% 100%;
            transition: all 0.3s ease;
          }
          .reply-count.expanded::after {
            transform: translateY(-50%) rotate(0);
          }
          .bottom-extra {
            line-height: 22px;
            height: 22px;
          }
          .reply {
            color: #1f2129;
            cursor: pointer;
          }
          .report,
          .delete-comment {
            position: relative;
            margin-right: 20px;
            font-size: 16px;
            color: #979aa8;
            cursor: pointer;
          }
          .report:hover,
          .delete-comment:hover {
            color: #1f2129;
          }
          .digg {
            position: relative;
            padding-left: 20px;
            color: #979aa8;
            cursor: pointer;
          }
          .digged {
            color: #979aa8;
          }
          .digg::after {
            content: '';
            position: absolute;
            top: 3px;
            left: 0;
            width: 16px;
            height: 16px;
            background-image: url('${thumb_up_icon}');
            background-size: 100% 100%;
          }
          .digged::after {
            background-image: url('${thumb_up_active_icon}');
          }
          .reply-input.show {
            margin-top: 20px;
          }
        `}
      </style>
    </div>
  )
}

export default Reply
